import React, { useState } from 'react';
import { get, isEmpty } from 'lodash';
import { TextField } from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import frLocale from 'date-fns/locale/fr';
import moment from 'moment-timezone';
import { injectIntl, IntlShape } from 'react-intl';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { DATE_FORMAT, DATE_PICKET_FORMAT } from 'constants/global';
import { dateStringToLocalDate } from 'containers/IdentificationPage/utils';
import messages from 'containers/LawyerLegalProceedingsPage/messages';
import { translate } from 'containers/services/translate';
import { JudgementOnMeritsType } from 'containers/LawyerLegalProceedingsPage/typings';
import { JudgementADDDocumentType } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import { getLastJudgementDate } from 'containers/LawyerLegalProceedingsPage/utils';
import { getDateIgnoreTimezone } from 'containers/common/LegalProceedings/utils';
import { validationCourtFileNumber } from 'containers/common/LegalProceedings/JudgementAdd/Forms/ProcedureInformation/utils';

export interface JudgementOnMeritsInfoProps {
  intl: IntlShape;
  judgementOnMeritsInfo: any;
  setJudgementOnMeritsInfo: Function;
  judgementOnMerits: JudgementOnMeritsType;
  judgementAddDocuments?: JudgementADDDocumentType[];
}
const JudgementOnMeritsInfo: React.FC<JudgementOnMeritsInfoProps> = ({
  judgementOnMeritsInfo,
  judgementOnMerits,
  setJudgementOnMeritsInfo,
  ...props
}): JSX.Element => {
  const [errorDate, setErrorDate] = useState(false);

  const [judgementNumber, setJudgementNumber] = useState<string>(
    get(judgementOnMeritsInfo, 'judgementNumber', '') ||
      (judgementOnMerits?.judgementOnMerits === undefined
        ? ''
        : get(
            judgementOnMerits['judgementOnMerits']['jom'],
            'judgementNumber',
            '',
          )),
  );
  const [judgementDate, setJudgementDate] = useState<string>('');

  const requiredFieldMessage = translate(messages.validateRequiredField, props);

  const handleChange = (e: React.ChangeEvent<{ value: string }>) => {
    setJudgementNumber(e.target.value);
    setJudgementOnMeritsInfo({
      ...judgementOnMeritsInfo,
      judgementNumber: e.target.value,
    });
  };

  const handleChangeDate = (date: Date | null) => {
    const theDate = getDateIgnoreTimezone(date);
    setErrorDate(false);
    const judgementDate = moment(theDate as Date).format(DATE_FORMAT);
    setJudgementDate(judgementDate);
    setJudgementOnMeritsInfo({ ...judgementOnMeritsInfo, judgementDate });

    if (!isEmpty(props.judgementAddDocuments)) {
      const jaddJudgementDate = getLastJudgementDate(
        get(props, 'judgementAddDocuments', []),
      );
      if (
        new Date(theDate) > new Date() ||
        (jaddJudgementDate && new Date(theDate) < new Date(jaddJudgementDate))
      ) {
        setErrorDate(true);
      }
    }
  };

  const judgementNumberInputProps = isEmpty(judgementOnMerits)
    ? { onChange: handleChange, value: judgementNumber }
    : {
        value: get(
          judgementOnMerits['judgementOnMerits']['jom'],
          'judgementNumber',
          '',
        ),
        readOnly: true,
      };

  return (
    <>
      <div className="correctional-judgement">
        <TextField
          id="judgement_number_field"
          label={translate(messages.judgementOnMeritsNumberFieldTitle, props)}
          InputProps={judgementNumberInputProps}
          placeholder={'NNNNNN/NNNN/NNNN'}
          error={
            judgementNumber === '' ||
            !validationCourtFileNumber(judgementNumber)
          }
          helperText={
            judgementNumber === ''
              ? requiredFieldMessage
              : !validationCourtFileNumber(judgementNumber)
              ? translate(messages.courtFileNumberError, props)
              : ''
          }
        />
        <div className="judgement-on-merits-info">
          {isEmpty(judgementOnMerits) ? (
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
              <KeyboardDatePicker
                autoOk
                disableFuture
                minDate={getLastJudgementDate(
                  get(props, 'judgementAddDocuments', []),
                )}
                variant="inline"
                format={DATE_PICKET_FORMAT}
                id="form-judgement-date"
                onChange={handleChangeDate}
                value={dateStringToLocalDate(judgementDate)}
                label={translate(messages.judgementDate, props)}
                error={
                  judgementDate === '' ||
                  judgementDate === 'Invalid date' ||
                  errorDate
                }
                helperText={
                  judgementDate === ''
                    ? requiredFieldMessage
                    : judgementDate === 'Invalid date'
                    ? translate(messages.invalidDate, props)
                    : errorDate
                    ? translate(messages.judgementDateError, props)
                    : ''
                }
              />
            </MuiPickersUtilsProvider>
          ) : (
            get(
              judgementOnMerits['judgementOnMerits']['jom'],
              'judgementDate',
              '',
            ) && (
              <TextField
                id="judgement_date_field"
                label={translate(messages.judgementDate, props)}
                value={get(
                  judgementOnMerits['judgementOnMerits']['jom'],
                  'judgementDate',
                  '',
                ).substring(0, 10)}
                InputProps={{ readOnly: true }}
              />
            )
          )}
        </div>
      </div>
    </>
  );
};

export default injectIntl(JudgementOnMeritsInfo);
